import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import FileUpload from '../../../components/ui/FileUpload';


const FloorTypeForm = ({ floorType, categories, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    thumbnail: '',
    thumbnailAlt: '',
    price: '',
    priceUnit: 'per sq ft',
    category: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  useEffect(() => {
    if (floorType) {
      setFormData(floorType);
      setThumbnailPreview(floorType?.thumbnail);
    }
  }, [floorType]);

  const priceUnitOptions = [
    { value: 'per sq ft', label: 'Per Square Foot' },
    { value: 'per sq meter', label: 'Per Square Meter' },
    { value: 'per project', label: 'Per Project' },
    { value: 'custom', label: 'Custom Unit' }
  ];

  const categoryOptions = categories?.map(cat => ({
    value: cat,
    label: cat
  }));

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleThumbnailUpload = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader?.result);
      handleInputChange('thumbnail', reader?.result);
    };
    reader?.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Floor type name is required';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData?.thumbnail) {
      newErrors.thumbnail = 'Thumbnail image is required';
    }

    if (!formData?.thumbnailAlt?.trim()) {
      newErrors.thumbnailAlt = 'Image description is required for accessibility';
    }

    if (!formData?.price || parseFloat(formData?.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData?.category) {
      newErrors.category = 'Category selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        id: floorType?.id || Date.now(),
        createdAt: floorType?.createdAt || new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Input
            label="Floor Type Name"
            type="text"
            placeholder="e.g., Metallic Epoxy Premium"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Description
            </label>
            <textarea
              className="w-full px-4 py-3 bg-background border border-input rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 min-h-[120px]"
              placeholder="Detailed description of the floor type, its features, and benefits..."
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
            />
            {errors?.description && (
              <p className="text-sm text-error mt-1">{errors?.description}</p>
            )}
          </div>

          <Select
            label="Category"
            options={categoryOptions}
            value={formData?.category}
            onChange={(value) => handleInputChange('category', value)}
            error={errors?.category}
            placeholder="Select floor type category"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Price"
              type="number"
              placeholder="0.00"
              value={formData?.price}
              onChange={(e) => handleInputChange('price', e?.target?.value)}
              error={errors?.price}
              required
              min="0"
              step="0.01"
            />

            <Select
              label="Price Unit"
              options={priceUnitOptions}
              value={formData?.priceUnit}
              onChange={(value) => handleInputChange('priceUnit', value)}
            />
          </div>
        </div>

        <div className="space-y-6">
          <FileUpload
            label="Thumbnail Image"
            description="Upload a high-quality image (max 5MB). Recommended size: 800x600px"
            accept="image/*"
            maxSize={5242880}
            onUpload={handleThumbnailUpload}
            error={errors?.thumbnail}
          />

          {thumbnailPreview && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-text-primary">
                Preview
              </label>
              <div className="relative h-48 bg-muted rounded-lg overflow-hidden border border-border">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          <Input
            label="Image Description (Alt Text)"
            type="text"
            placeholder="Describe the image for accessibility (e.g., Glossy metallic blue epoxy floor with silver flakes in modern garage)"
            value={formData?.thumbnailAlt}
            onChange={(e) => handleInputChange('thumbnailAlt', e?.target?.value)}
            error={errors?.thumbnailAlt}
            description="Provide a detailed description of the image for screen readers"
            required
          />

          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
            <input
              type="checkbox"
              id="isActive"
              checked={formData?.isActive}
              onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
              className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-ring"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-text-primary cursor-pointer">
              Set as active (visible to customers)
            </label>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          iconName="Save"
          iconPosition="left"
          iconSize={16}
        >
          {floorType ? 'Update Floor Type' : 'Create Floor Type'}
        </Button>
      </div>
    </form>
  );
};

export default FloorTypeForm;